import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatISODate from '../../../Helpers/formatISODate'
import './styles.scss'

const STEPS = [
  { label: 'Picked Up', icon: solid('box') },
  { label: 'In Transit', icon: solid('plane') },
  { label: 'Arrived', icon: solid('truck-fast') },
  { label: 'Delivered', icon: solid('circle-check') },
]

const STATUS_STEP = {
  'Inactive': -1,
  'Picked Up': 0,
  'Active': 1,
  'On hold': 1,
  'Arrived': 2,
  'Delivered': 3,
}

const getStepIndex = (status) => STATUS_STEP[status] ?? -1

const getBadgeClass = (status) => {
  switch (status) {
    case 'Delivered': return 'badge--success'
    case 'Arrived': return 'badge--warning'
    case 'Active':
    case 'Picked Up': return 'badge--info'
    case 'On hold': return 'badge--danger'
    default: return 'badge--neutral'
  }
}

const InfoCard = ({ title, icon, rows }) => (
  <div className="info-card">
    <div className="info-card__header">
      <FontAwesomeIcon icon={icon} />
      <span>{title}</span>
    </div>
    <div className="info-card__body">
      {rows.map(({ label, value }) => (
        <div className="info-card__row" key={label}>
          <span className="info-card__label">{label}</span>
          <span className="info-card__value">{value || '—'}</span>
        </div>
      ))}
    </div>
  </div>
)

const TrackItResult = ({ trackItResult, onReset }) => {
  const stepIndex = getStepIndex(trackItResult.status)
  const badgeClass = getBadgeClass(trackItResult.status)
  const dispatchDate = formatISODate(trackItResult.dispatchDate)?.split(' ')[0] || '—'
  const etaDate = formatISODate(trackItResult.estimatedDeliveryDate)?.split(' ')[0] || '—'

  return (
    <div className="track-result">
      <div className="track-result__header">
        <div className="track-result__header__left">
          <span className="track-result__number">
            <FontAwesomeIcon icon={solid('barcode')} />
            {trackItResult.trackingNumber}
          </span>
          <span className={`track-result__badge ${badgeClass}`}>
            {trackItResult.status}
          </span>
        </div>
        <button className="track-result__back" onClick={onReset}>
          <FontAwesomeIcon icon={solid('arrow-left')} />
          Track Another
        </button>
      </div>

      <div className="track-result__stepper">
        {STEPS.map((step, i) => (
          <div
            key={step.label}
            className={[
              'stepper-step',
              i <= stepIndex ? 'stepper-step--done' : '',
              i === stepIndex ? 'stepper-step--active' : '',
            ].join(' ')}
          >
            <div className="stepper-step__node">
              <div className="stepper-step__circle">
                <FontAwesomeIcon icon={step.icon} />
              </div>
              {i < STEPS.length - 1 && (
                <div className={`stepper-step__line ${i < stepIndex ? 'stepper-step__line--done' : ''}`} />
              )}
            </div>
            <span className="stepper-step__label">{step.label}</span>
          </div>
        ))}
      </div>

      <div className="track-result__cards">
        <InfoCard
          title="Sender"
          icon={solid('user')}
          rows={[
            { label: 'Name', value: trackItResult.senderName },
            { label: 'Address', value: trackItResult.senderAddress },
          ]}
        />
        <InfoCard
          title="Receiver"
          icon={solid('user-check')}
          rows={[
            { label: 'Name', value: trackItResult.receiverName },
            { label: 'Address', value: trackItResult.receiverAddress },
          ]}
        />
      </div>

      <div className="track-result__details">
        <div className="track-result__details__header">
          <FontAwesomeIcon icon={solid('box-open')} />
          <span>Shipment Details</span>
        </div>
        <div className="track-result__details__grid">
          <div className="detail-item">
            <span className="detail-item__label">Description</span>
            <span className="detail-item__value">{trackItResult.packageDescription || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item__label">Current Location</span>
            <span className="detail-item__value">{trackItResult.currentLocation || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item__label">Dispatch Date</span>
            <span className="detail-item__value">{dispatchDate}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item__label">Estimated Delivery</span>
            <span className="detail-item__value">{etaDate}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrackItResult
